import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Quote } from "lucide-react";
import PageTransition from "../components/PageTransition";
import { Laurel } from "../components/Laurel";

const AWARDS = [
  { id: "miff", festival: "19th Mumbai International Film Festival", year: "2026", film: "The Legend of Birsa Munda", type: "Official Selection · Non-Competition", image: "/awards/miff-laurel.png" },
  { id: "nomorefrog", festival: "NOMOREFROG AI Film Awards · South Korea", year: "2026", film: "Baraat", type: "Official Selection", image: "/awards/nomorefrog-laurel.png" },
  { id: "miami", festival: "Miami Art Tech Summit", year: "2025", film: "The Legend of Birsa Munda", type: "Official Selection", image: "/awards/miami-laurel.png" },
  { id: "iffi", festival: "AI Film Festival · IFFI 2025", year: "2025", film: "The Legend of Birsa Munda", type: "Official Showcase" },
  { id: "ai-impact", festival: "India AI Impact Summit", year: "2025", film: "The Legend of Birsa Munda", type: "Official Showcase" },
  { id: "budapest", festival: "Budapest SmartFest", year: "2025", film: "The Legend of Birsa Munda", type: "Official Selection" },
  { id: "iffd", festival: "AI Filmmaking Hackathon · IFFD 2026", year: "2026", film: "The Window", type: "48-Hour Film" },
];

const CERTIFICATE = {
  id: "cinemai",
  festival: "CinemAI Hackathon · Waves Film Bazaar, IFFI Goa",
  year: "2025",
  film: "Lost & Found",
  type: "Best Storytelling Award",
  image: "/awards/lost-and-found-certificate.jpg",
};

const STORY_IMAGE = "/about/craft-editing.png";

const TIMELINE = [
  { year: "2026", title: "Cine Prompt Founded", desc: "An independent AI filmmaking venture is started in Hyderabad — one person, one laptop, international ambition." },
  { year: "2026", title: "The Last Drop", desc: "The debut film — a personal reckoning with the global water crisis — releases on YouTube." },
  { year: "2025", title: "The Legend of Birsa Munda", desc: "A film about India's legendary tribal revolutionary wins multiple awards and travels the world." },
  { year: "2025", title: "India AI Impact Summit · IFFI 2025", desc: "Birsa Munda is officially showcased at the India AI Impact Summit and the AI Film Festival at IFFI 2025." },
  { year: "2025", title: "Miami & Budapest", desc: "Birsa Munda is selected for the Miami Art Tech Summit and Budapest SmartFest." },
  { year: "2025", title: "Best Storytelling — IFFI Goa", desc: "Lost & Found wins Best Storytelling at the CinemAI Hackathon at IFFI Goa — made in 48 hours." },
  { year: "2026", title: "The Window", desc: "Created in 48 hours at the AI Filmmaking Hackathon, IFFD 2026 — a meditation on perspective and connection." },
  { year: "2026", title: "Arunima & Built in Silence", desc: "Two new releases — a tribute to Arunima Sinha, and a cinematic AI spec ad for a sports brand." },
  { year: "2026", title: "19th Mumbai International Film Festival", desc: "The Legend of Birsa Munda is officially selected in the Non-Competition Section of the 19th Mumbai International Film Festival, 2026." },
  { year: "2026", title: "Baraat & Viraam", desc: "Baraat is officially selected at the NOMOREFROG AI Film Awards, South Korea. Viraam is created for the Higgsfield AI Film Festival." },
];

const FILMMAKER = {
  name: "Samresh Shrivastav",
  role: "Independent AI Filmmaker & Creator",
  image: "/about/filmmaker-at-desk.png",
  tools: ["Midjourney", "Kling", "Runway", "Seedance", "NanoBanana", "ElevenLabs", "Suno"],
};

/* ------------------ Statement Hero ------------------ */
const Statement = () => (
  <section className="relative bg-ink pt-40 pb-16 md:pt-56 md:pb-24" data-testid="about-statement">
    <div className="mx-auto max-w-[1500px] px-6 md:px-12">
      <div className="overflow-hidden">
        <motion.h1
          initial={{ y: "8%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1.2, ease: [0.22, 0.65, 0.2, 1] }}
          className="font-serif text-bone leading-[0.92] tracking-tight"
          style={{ fontSize: "clamp(2.6rem, 8.4vw, 8rem)" }}
          data-testid="about-statement-headline"
        >
          We don't just <span className="italic text-gold">make</span> films.
          <br />
          We <span className="italic text-gold">engineer</span> stories.
        </motion.h1>
      </div>
    </div>
  </section>
);

/* ------------------ Two Column Story ------------------ */
const StoryColumns = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <section ref={ref} className="relative bg-ink py-24 md:py-36" data-testid="about-story">
      <div className="mx-auto max-w-[1500px] px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        {/* Tall editorial image (parallax) */}
        <div className="md:col-span-5">
          <div
            className="cp-frame relative w-full overflow-hidden border border-gold/20"
            style={{ aspectRatio: "3 / 4.6" }}
            data-testid="about-story-image"
          >
            <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
              <img loading="lazy" decoding="async"
                src={STORY_IMAGE}
                alt="A still from the studio archive"
                className="w-full h-full object-cover"
                style={{ filter: "saturate(0.78) contrast(1.08) brightness(0.78)" }}
              />
            </motion.div>
            {/* Local film-grain wash on the image */}
            <div
              className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-50"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.9 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
                backgroundSize: "180px 180px",
              }}
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
            {/* Caption tag */}
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-bone/75 text-[10px] tracking-[0.3em] uppercase">
              <span>Studio · 04:12 AM</span>
              <span className="text-gold">35 mm · 1.85 : 1</span>
            </div>
          </div>
        </div>

        {/* Story text */}
        <div className="md:col-span-7 md:pt-8">
          <p className="overline mb-5" data-testid="about-story-overline">01 · Studio</p>
          <h2
            className="font-serif text-bone leading-[1.05] tracking-tight"
            style={{ fontSize: "clamp(1.9rem, 4vw, 3.4rem)" }}
          >
            <span className="text-gold italic">Cine Prompt</span> is an independent
            AI filmmaking venture started in 2026 by Samresh Shrivastav.
          </h2>
          <div className="hairline mt-10 mb-10 max-w-md" />
          <p className="text-bone/75 font-sans text-base md:text-lg leading-relaxed max-w-2xl">
            What began as one person, one laptop, and one story about the global water crisis —
            has grown into an award-winning body of work screened at international festivals
            across India, USA, and Europe.
          </p>
          <p className="text-bone/65 font-sans text-base md:text-lg leading-relaxed max-w-2xl mt-6">
            At Cine Prompt, we believe technology should serve the story — never the other way
            around. AI gives us the power to produce films without a crew, without a massive
            budget, and without compromise. But the soul of every film? That's entirely human.
          </p>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-6 max-w-xl">
            <div>
              <p className="overline mb-2">Founded</p>
              <p className="font-serif text-bone text-2xl">2026</p>
            </div>
            <div>
              <p className="overline mb-2">Based in</p>
              <p className="font-serif text-bone text-2xl">Hyderabad</p>
            </div>
            <div>
              <p className="overline mb-2">Released</p>
              <p className="font-serif text-bone text-2xl">6 films</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------ Pull Quote ------------------ */
const PullQuote = () => (
  <section className="relative bg-ink py-24 md:py-36 border-y border-gold/20" data-testid="about-pullquote">
    <div className="mx-auto max-w-[1300px] px-6 md:px-12 text-center">
      <Quote className="text-gold mx-auto mb-8" size={28} strokeWidth={1.2} />
      <motion.blockquote
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease: [0.22, 0.65, 0.2, 1] }}
        className="font-serif italic text-gold leading-[1] tracking-tight"
        style={{ fontSize: "clamp(2.2rem, 6vw, 5.5rem)" }}
        data-testid="about-pullquote-text"
      >
        "The camera is no longer a machine. <br className="hidden md:block" />
        It's a conversation between the storyteller and the infinite."
      </motion.blockquote>
      <p className="mt-10 overline">— Samresh Shrivastav, Founder, Cine Prompt</p>
    </div>
  </section>
);

/* ------------------ Timeline ------------------ */
const Timeline = () => (
  <section className="relative bg-ink py-24 md:py-36" data-testid="about-timeline">
    <div className="mx-auto max-w-[1500px] px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12 md:mb-20">
        <div className="md:col-span-4">
          <p className="overline mb-4">02 · Journey</p>
          <h2
            className="font-serif text-bone leading-[1.02] tracking-tight"
            style={{ fontSize: "clamp(2.2rem, 5vw, 4.5rem)" }}
          >
            A short <span className="italic text-gold">history</span> of frames.
          </h2>
        </div>
        <div className="md:col-span-7 md:col-start-6 self-end">
          <p className="text-bone/65 font-sans text-base md:text-lg leading-relaxed max-w-xl">
            Six years, two studios, an unreasonable number of late nights and one studio dog named Linus.
          </p>
        </div>
      </div>

      <ol className="relative" data-testid="about-timeline-list">
        {/* central rail */}
        <span className="absolute left-3 md:left-1/2 top-2 bottom-2 w-px bg-gold/25 -translate-x-0 md:-translate-x-1/2" aria-hidden="true" />

        {TIMELINE.map((item, i) => (
          <motion.li
            key={item.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: i * 0.05, ease: [0.22, 0.65, 0.2, 1] }}
            className={`relative pl-12 md:pl-0 md:grid md:grid-cols-2 md:gap-16 py-10 ${
              i !== TIMELINE.length - 1 ? "border-b border-gold/10" : ""
            }`}
            data-testid={`about-timeline-item-${item.year}`}
          >
            {/* bullet */}
            <span
              className="absolute left-3 md:left-1/2 top-12 w-3 h-3 -translate-x-1/2 bg-ink border border-gold/70 rounded-full"
              aria-hidden="true"
            >
              <span className="absolute inset-0.5 rounded-full bg-gold/70" />
            </span>

            {i % 2 === 0 ? (
              <>
                <div className="md:text-right md:pr-12 mb-2 md:mb-0">
                  <p className="overline">{item.year}</p>
                  <h3 className="font-serif text-bone text-3xl md:text-4xl mt-2 leading-tight">
                    {item.title}
                  </h3>
                </div>
                <p className="md:pl-12 text-bone/65 font-sans text-sm md:text-base leading-relaxed max-w-md">
                  {item.desc}
                </p>
              </>
            ) : (
              <>
                <p className="md:text-right md:pr-12 text-bone/65 font-sans text-sm md:text-base leading-relaxed md:order-1 max-w-md md:ml-auto">
                  {item.desc}
                </p>
                <div className="md:pl-12 md:order-2 mb-2 md:mb-0">
                  <p className="overline">{item.year}</p>
                  <h3 className="font-serif text-bone text-3xl md:text-4xl mt-2 leading-tight">
                    {item.title}
                  </h3>
                </div>
              </>
            )}
          </motion.li>
        ))}
      </ol>
    </div>
  </section>
);

/* ------------------ Festivals & Awards ------------------ */
const AwardTile = ({ a, index }) => (
  <motion.li
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.6, delay: index * 0.06, ease: [0.22, 0.65, 0.2, 1] }}
    className="group border border-gold/15 hover:border-gold/45 bg-ink/60 transition-colors duration-500 flex flex-col"
    data-testid={`award-tile-${a.id}`}
  >
    <div className="relative flex items-center justify-center px-5 md:px-6 h-[170px] md:h-[190px]">
      {a.image ? (
        <img
          src={a.image}
          alt={`${a.type} — ${a.festival}`}
          loading="lazy"
          decoding="async"
          className="w-full max-h-[110px] md:max-h-[130px] object-contain opacity-85 group-hover:opacity-100 transition-opacity duration-500"
        />
      ) : (
        <Laurel lg top={a.type} label={a.festival} className="!bg-transparent !border-0 !px-0 !backdrop-blur-none" />
      )}
    </div>
    <div className="border-t border-gold/10 px-5 py-4">
      <p className="text-gold text-[10px] tracking-[0.3em] uppercase">{a.type}</p>
      <p className="font-serif text-bone text-base md:text-lg leading-snug mt-1.5">{a.festival}</p>
      <p className="text-bone/55 font-sans text-[10px] tracking-[0.25em] uppercase mt-2">
        {a.film} · {a.year}
      </p>
    </div>
  </motion.li>
);

const Awards = () => (
  <section className="relative bg-ink py-24 md:py-36 border-t border-gold/10" data-testid="about-awards">
    <div className="mx-auto max-w-[1500px] px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12 md:mb-20">
        <div className="md:col-span-5">
          <p className="overline mb-4">03 · Festivals & Awards</p>
          <h2
            className="font-serif text-bone leading-[1.02] tracking-tight"
            style={{ fontSize: "clamp(2.2rem, 5vw, 4.5rem)" }}
            data-testid="about-awards-headline"
          >
            Laurels, <span className="italic text-gold">earned</span> frame by frame.
          </h2>
        </div>
        <div className="md:col-span-6 md:col-start-7 self-end">
          <p className="text-bone/65 font-sans text-base md:text-lg leading-relaxed max-w-xl">
            From Goa to Miami, Budapest to Seoul and back home to Mumbai — every selection is a
            reminder that AI films, made with soul, travel.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
        <motion.figure
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7, ease: [0.22, 0.65, 0.2, 1] }}
          className="lg:col-span-4 lg:self-start border border-gold/20 bg-ink/60 p-5 md:p-6 flex flex-col"
          data-testid="award-certificate-cinemai"
        >
          <div className="cp-frame relative overflow-hidden border border-bone/10">
            <img
              src={CERTIFICATE.image}
              alt={`${CERTIFICATE.type} certificate — ${CERTIFICATE.film}`}
              loading="lazy"
              decoding="async"
              className="w-full h-auto block"
            />
          </div>
          <figcaption className="pt-5 mt-auto">
            <p className="text-gold text-[10px] tracking-[0.3em] uppercase">{CERTIFICATE.type}</p>
            <p className="font-serif text-bone text-xl md:text-2xl leading-snug mt-1.5">{CERTIFICATE.festival}</p>
            <p className="text-bone/55 font-sans text-[10px] tracking-[0.25em] uppercase mt-2">
              {CERTIFICATE.film} · {CERTIFICATE.year}
            </p>
          </figcaption>
        </motion.figure>

        <ul className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8" data-testid="about-awards-grid">
          {AWARDS.map((a, i) => (
            <AwardTile key={a.id} a={a} index={i} />
          ))}
        </ul>
      </div>
    </div>
  </section>
);

/* ------------------ The Filmmaker (Samresh) ------------------ */
const Filmmaker = () => (
  <section className="relative bg-ink py-24 md:py-36 border-t border-gold/15" data-testid="about-filmmaker">
    <div className="mx-auto max-w-[1500px] px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        {/* Portrait */}
        <div className="md:col-span-5">
          <div
            className="cp-frame relative w-full overflow-hidden border border-gold/20"
            style={{ aspectRatio: "3 / 4.2" }}
            data-testid="about-filmmaker-image"
          >
            <img
              loading="lazy"
              decoding="async"
              src={FILMMAKER.image}
              alt={FILMMAKER.name}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: "saturate(0.78) contrast(1.08) brightness(0.78)" }}
            />
            <div
              className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-50"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.9 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
                backgroundSize: "180px 180px",
              }}
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-bone/75 text-[10px] tracking-[0.3em] uppercase">
              <span>Hyderabad · 04:12 AM</span>
              <span className="text-gold">CP · 01</span>
            </div>
          </div>
        </div>

        {/* Bio */}
        <div className="md:col-span-7 md:pt-2">
          <p className="overline mb-5">03 · The Filmmaker</p>
          <h2
            className="font-serif text-bone leading-[1] tracking-tight"
            style={{ fontSize: "clamp(2.2rem, 5vw, 4.5rem)" }}
          >
            {FILMMAKER.name}
          </h2>
          <p className="mt-3 text-gold font-sans text-sm tracking-[0.22em] uppercase">
            {FILMMAKER.role}
          </p>

          <div className="hairline my-10 max-w-md" />

          <p className="text-bone/75 font-sans text-base md:text-lg leading-relaxed max-w-2xl">
            A self-taught AI filmmaker from Hyderabad, Samresh discovered the power of
            generative AI tools to bring untold stories to life. He crafts every frame,
            every voice, and every score himself — a one-person studio with international ambition.
          </p>
          <p className="text-bone/65 font-sans text-base md:text-lg leading-relaxed max-w-2xl mt-5">
            His debut film, <span className="italic text-bone/85">The Last Drop</span>, was a
            personal reckoning with the global water crisis. His second film,{" "}
            <span className="italic text-bone/85">The Legend of Birsa Munda</span>, went on to
            win multiple awards and was officially showcased at the India AI Impact Summit and
            the AI Film Festival at IFFI 2025, selected for the Miami Art Tech Summit and
            Budapest SmartFest, and officially selected in the Non-Competition Section of the
            19th Mumbai International Film Festival, 2026.
          </p>
          <p className="text-bone/65 font-sans text-base md:text-lg leading-relaxed max-w-2xl mt-5">
            For Samresh, AI filmmaking is not just a craft — it's a democratic revolution.
          </p>

          <figure className="relative mt-10 pl-6">
            <span className="absolute left-0 top-0 bottom-0 w-px bg-gold/60" aria-hidden="true" />
            <blockquote className="font-serif italic text-bone leading-[1.25] text-xl md:text-2xl">
              "Amazing how this is totally democratising the art of filmmaking. For the first
              time, a single person with a story can make a film that reaches the world."
            </blockquote>
            <figcaption className="overline mt-4">— Samresh Shrivastav</figcaption>
          </figure>

          <div className="mt-12">
            <p className="overline mb-4">The Toolkit</p>
            <ul className="flex flex-wrap gap-2 md:gap-3" data-testid="about-filmmaker-tools">
              {FILMMAKER.tools.map((t) => (
                <li
                  key={t}
                  className="px-4 py-2 border border-gold/30 text-bone/85 text-[11px] tracking-[0.22em] uppercase font-sans hover:border-gold hover:text-gold transition-colors duration-300"
                >
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ------------------ Closing CTA ------------------ */
const ClosingCTA = () => (
  <section className="relative bg-ink py-32 md:py-44 border-t border-gold/20" data-testid="about-closing-cta">
    <div className="mx-auto max-w-[1300px] px-6 md:px-12 text-center">
      <p className="overline mb-6">Open call · Spring MMXXVI</p>
      <h2
        className="font-serif text-bone tracking-tight leading-[1] mx-auto max-w-4xl"
        style={{ fontSize: "clamp(2.4rem, 6.5vw, 5.5rem)" }}
      >
        Want to make something <span className="italic text-gold">strange</span> with us?
      </h2>
      <div className="mt-10 flex flex-wrap items-center justify-center gap-5">
        <Link to="/contact" className="cp-btn cp-btn--glow" data-testid="about-cta-collaborate">
          Begin a project <ArrowUpRight size={14} />
        </Link>
        <Link to="/work" className="cp-btn cp-btn--ghost" data-testid="about-cta-work">
          See our work
        </Link>
      </div>
    </div>
  </section>
);

const About = () => (
  <PageTransition testId="page-about">
    <Statement />
    <StoryColumns />
    <PullQuote />
    <Filmmaker />
    <Timeline />
    <Awards />
    <ClosingCTA />
  </PageTransition>
);

export default About;
